import { create } from "zustand";
import { syncQueuedNotes, getNotesQueueCounts } from "./noteSync";
import { useOnlineStatus } from "./useOnlineStatus";
import { useEffect } from "react";
import { useToastStore } from "@/ui/toastStore";
import { refreshDownloadedPrefixes } from "./offlineRefresh";

interface NoteSyncState {
  pendingCount: number;
  failedCount: number;
  syncing: boolean;
  lastError: string | null;
  lastSyncAt: string | null;
  refreshCounts: () => Promise<void>;
  syncNow: (opts?: { includeFailed?: boolean }) => Promise<void>;
}

export const useNoteSyncStore = create<NoteSyncState>((set, get) => ({
  pendingCount: 0,
  failedCount: 0,
  syncing: false,
  lastError: null,
  lastSyncAt: null,

  async refreshCounts() {
    const { pendingCount, failedCount } = await getNotesQueueCounts();
    set({ pendingCount, failedCount });
  },

  async syncNow(opts) {
    if (get().syncing) return;

    const pushToast = useToastStore.getState().push;
    const beforePending = get().pendingCount;
    const beforeFailed = get().failedCount;

    set({ syncing: true, lastError: null });
    try {
      await syncQueuedNotes({ includeFailed: opts?.includeFailed ?? true });
      await get().refreshCounts();
      set({ lastSyncAt: new Date().toISOString() });
      // After pushing outbound notes, pull latest inbound changes.
      await refreshDownloadedPrefixes();

      const afterPending = get().pendingCount;
      const afterFailed = get().failedCount;

      if (beforePending + beforeFailed > 0) {
        if (afterPending + afterFailed === 0) {
          pushToast("All notes synced", "success");
        } else if (afterFailed > 0) {
          pushToast("Some notes failed to sync", "error");
        }
      }
    } catch (err) {
      set({
        lastError: err instanceof Error ? err.message : typeof err === "string" ? err : "Sync failed",
      });
      pushToast("Note sync failed", "error");
    } finally {
      set({ syncing: false });
    }
  },
}));

export function useNoteSyncBootstrap() {
  const online = useOnlineStatus();
  const pendingCount = useNoteSyncStore((s) => s.pendingCount);
  const failedCount = useNoteSyncStore((s) => s.failedCount);
  const refreshCounts = useNoteSyncStore((s) => s.refreshCounts);
  const syncNow = useNoteSyncStore((s) => s.syncNow);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  useEffect(() => {
    if (online) {
      syncNow({ includeFailed: true });
    }
  }, [online, syncNow]);

  useEffect(() => {
    const handler = () => syncNow({ includeFailed: true });
    window.addEventListener("online", handler);
    return () => window.removeEventListener("online", handler);
  }, [syncNow]);

  useEffect(() => {
    if (!online) return;
    const outstanding = pendingCount + failedCount;
    if (!outstanding) return;

    const id = window.setInterval(() => {
      syncNow({ includeFailed: true });
    }, 5 * 60 * 1000);

    return () => window.clearInterval(id);
  }, [online, pendingCount, failedCount, syncNow]);
}
